import React, { useState, useRef, useEffect, useCallback } from "react";
import { View, TextInput, StyleSheet, Platform, Pressable, ViewStyle } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";

export interface TableSearchBarProps {
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  style?: ViewStyle;
}

export function TableSearchBar({
  value,
  onChangeText,
  placeholder = "Search...",
  debounceMs = 300,
  style,
}: TableSearchBarProps) {
  const { colors } = useTheme();
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeTextRef = useRef(onChangeText);
  onChangeTextRef.current = onChangeText;

  // Sync external value when not typing
  useEffect(() => {
    if (value !== localValue && !timeoutRef.current) {
      setLocalValue(value);
    }
  }, [value, localValue]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleChangeText = useCallback(
    (text: string) => {
      setLocalValue(text);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onChangeTextRef.current(text);
        timeoutRef.current = null;
      }, debounceMs);
    },
    [debounceMs],
  );

  const handleClear = useCallback(() => {
    setLocalValue("");
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    onChangeTextRef.current("");
  }, []);

  const borderColor = isFocused ? colors.primary : colors.border;

  return (
    <View style={[styles.container, { borderColor, backgroundColor: colors.card }, style]}>
      <MaterialCommunityIcons
        name="magnify"
        size={20}
        color={isFocused ? colors.primary : colors.textSecondary}
        style={styles.searchIcon}
      />
      <TextInput
        value={localValue}
        onChangeText={handleChangeText}
        placeholder={placeholder}
        placeholderTextColor={colors.textSecondary}
        style={[styles.input, { color: colors.text }]}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        autoCapitalize="none"
        autoCorrect={false}
      />
      {localValue.length > 0 && (
        <Pressable onPress={handleClear} style={styles.clearButton}>
          <MaterialCommunityIcons name="close-circle" size={18} color={colors.textSecondary} />
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 40,
    marginBottom: 8,
  },
  searchIcon: {
    marginRight: 8,
  },
  input: {
    flex: 1,
    fontSize: 14,
    height: "100%",
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  clearButton: {
    padding: 4,
    marginLeft: 4,
  },
});
